import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { isAdmin, isCoordinator, isCoach } from '@/utils/roleChecks';
import type { User } from '@/types';

/**
 * Single source of truth for "may `viewer` see the feedback history of
 * `targetUid`?" — used by BOTH the route-level guard (FeedbackHistoryGuard,
 * Layer 1) and the component's own defensive read (StudentFeedbackHistory,
 * Layer 2). The two-layer pattern means a future route-config mistake can't
 * silently re-open the leak: the component re-verifies before it ever issues a
 * Firestore read.
 *
 * Context: /feedback-history/:uid? renders a user's coach ratings + free-text
 * coach notes (some about named children). Before the fix the route had NO
 * authorization — any authenticated caller could read any user's rating history
 * by URL manipulation.
 *
 * ALLOW when the viewer is:
 *   - the target themselves (or no target → defaults to self),
 *   - staff (super_admin / admin / coordinator / coach), or
 *   - a parent of the target child: target.parentId == viewer.uid, or
 *     viewer.uid in target.parentIds[]. This mirrors the Firestore `isParentOf`
 *     rule and reads the TARGET's doc rather than trusting a denormalized
 *     `viewer.childrenIds` that may not be populated on `currentUser`.
 * DENY everything else. A failed read fails CLOSED.
 */
export const canViewFeedbackHistory = async (
    viewer: User | null | undefined,
    targetUid: string | null | undefined,
): Promise<boolean> => {
    if (!viewer) return false;

    // Self — empty/missing target resolves to the viewer's own history.
    if (!targetUid || targetUid === viewer.uid) return true;

    // Staff (isAdmin covers super_admin; isCoordinator carries the
    // super-admin-uid whitelist; isCoach covers roleIds-based coaches).
    if (isAdmin(viewer) || isCoordinator(viewer) || isCoach(viewer)) return true;

    // Parent-of-target-child. Read the target doc and check the parent linkage
    // on it — the authoritative, denormalization-independent source.
    try {
        const snap = await getDoc(doc(db, 'users', targetUid));
        if (snap.exists()) {
            const data = snap.data() as { parentId?: string; parentIds?: string[] };
            if (data.parentId === viewer.uid) return true;
            if (Array.isArray(data.parentIds) && data.parentIds.includes(viewer.uid)) return true;
        }
    } catch (e) {
        // A failed read must fail CLOSED — fall through to deny.
        console.warn('canViewFeedbackHistory: parent-of-child check failed:', e);
    }

    return false;
};
